import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { TranslatePipe } from '../pipes/translate.pipe';
import { RestService } from '../services/rest.service';
import { SessionService } from '../services/session.service';
import { StorageService } from '../services/storage.service';
import { PaymentCodeService } from '../services/payment-code.service';
import { GtmAnalyticsService } from '../services/gtm-analytics.service';
import { TranslationService } from '../services/translation.service';
import { getCurrencySymbol } from '../admin/currencies';
import { fmtStoreAmount, priceNum, round2, savePct, storePair, webAnnualTotal } from './price-display';
import { ErrorReportingService } from '../services/error-reporting.service';
import {
  StoreBillingService,
  StorePlan,
  StoreProduct,
} from '../services/store-billing.service';
import { BrandService } from '../../services/brand-config.service';

export interface PlanConfig {
  id: string;
  name: string;
  price: string;
  oldPrice?: string;
  features: string[];
  buttonText: string;
  badge?: string;
}

export interface BundleConfig extends PlanConfig {
  numberOfTests: number;
  numberOfMonths: number;
}

@Component({
  selector: 'app-payment',
  standalone: true,
  imports: [CommonModule, MatButtonModule, TranslatePipe],
  templateUrl: './payment.component.html',
  styleUrl: './payment.component.scss',
})
export class PaymentComponent implements OnInit {
  scanId: string = '';
  isProcessing: boolean = false;
  processingPlanId: string | null = null;
  currencySymbol: string = '$';

  // Consumer paywall v2 (locked-flow spec §4b / PD-3): one-time 30-day pass +
  // recurring subscription with an annual/monthly selector. Bundle is B2B-only
  // and intentionally NOT rendered here.
  oneTimePrice: string = '';
  annualPrice: string = '';
  monthlyPrice: string = '';
  annualSavingLabel: string = '';
  // Prices load async (decode-price). Gate the plan cards on this so a slow connection doesn't
  // flash empty "$" amounts + the wrong default currency before the real prices arrive.
  pricesLoaded: boolean = false;
  // Which subscription billing the user is currently looking at in the selector.
  selectedBilling: 'annual' | 'monthly' = 'annual';

  // Native apps sell through the App Store / Google Play (store billing); the
  // web checkout is never shown inside the apps.
  storeMode = false;
  storeProducts: Partial<Record<StorePlan, StoreProduct>> = {};
  storeMessage = '';
  restoring = false;

  get subPrice(): string {
    return this.selectedBilling === 'annual'
      ? this.annualPrice
      : this.monthlyPrice;
  }
  get subBillingKey(): string {
    return this.selectedBilling === 'annual'
      ? 'payment.subscription.annualBilling'
      : 'payment.subscription.monthlyBilling';
  }
  get subButtonKey(): string {
    return this.selectedBilling === 'annual'
      ? 'payment.subscription.annualButton'
      : 'payment.subscription.monthlyButton';
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private restService: RestService,
    private sessionService: SessionService,
    private storageService: StorageService,
    private paymentCodeService: PaymentCodeService,
    private gtmAnalytics: GtmAnalyticsService,
    public translationService: TranslationService,
    private reporter: ErrorReportingService,
    public store: StoreBillingService,
    public brandService: BrandService,
  ) {
    console.log('PaymentComponent: initialized');
  }

  async ngOnInit() {
    this.scanId = this.route.snapshot.queryParams['scanId'] || '';

    if (this.store.enabled) {
      this.storeMode = true;
      try {
        this.storeProducts = await this.store.loadProducts();
      } catch (e) {
        this.reporter.report(e, 'payment.store');
      }
      if (!this.hasStoreProducts) {
        this.storeMessage = this.translationService.translate('payment.store.unavailable');
      } else if (!this.storeProducts.annual && this.storeProducts.monthly) {
        this.selectedBilling = 'monthly';
      }
      this.gtmAnalytics.trackPricingViewed();
      this.pricesLoaded = true;
      return;
    }

    // No scanId = ACCOUNT-LEVEL paywall entry (the "Open the full picture" upsell from home/profile).
    // The subscription plans (annual/monthly) are account-level and do NOT need a scan, so do NOT
    // bounce to new-test — just load prices and render the plans. Only the legacy per-scan one-time
    // unlock needs a scanId (guarded in its own handler); the scanId-dependent fallbacks below are
    // skipped when there's no scan.
    console.log(
      'PaymentComponent: loaded',
      this.scanId ? `for scanId: ${this.scanId}` : '(account-level paywall, no scanId)',
    );

    await this.paymentCodeService.saveCodeAndFetchPrice(
      undefined,
      this.scanId,
    );

    let priceData = this.storageService.getPriceData();

    if (!priceData) {
      console.log(
        'PaymentComponent: No price data in localStorage, fetching default prices from backend',
      );
      try {
        const response = await this.restService.request<any>({
          path: 'payment/decode-price',
          method: 'GET',
          queryParams: { ...(this.scanId ? { scanId: this.scanId } : {}), lang: this.translationService.getCurrentLanguage() },
        });

        if (response) {
          priceData = response;
          this.storageService.setPriceData(response);
          console.log(
            'PaymentComponent: Fetched and saved default price data',
            priceData,
          );
        } else if (this.scanId) {
          console.error(
            'PaymentComponent: No price response — back to the scan trial result',
          );
          this.router.navigate(['/app/trial-result', this.scanId]);
          return;
        }
      } catch (error) {
        console.error(
          'PaymentComponent: Error fetching default prices:',
          error,
        );
        this.reporter.report(error, 'payment');
        // Only the per-scan flow has somewhere to fall back to; the account-level paywall just
        // renders with default plan prices below.
        if (this.scanId) {
          this.router.navigate(['/app/trial-result', this.scanId]);
          return;
        }
      }
    }

    // Account-level paywall with no payment code may have no priceData — default it so the
    // subscription plans still render (the price fields below carry their own fallbacks).
    if (!priceData) {
      priceData = { currency: 'ILS' } as any;
    } else {
      console.log(
        'PaymentComponent: loaded price data from localStorage',
        priceData,
      );
    }

    this.currencySymbol = getCurrencySymbol(priceData.currency);
    console.log(
      'PaymentComponent: currency symbol set to:',
      this.currencySymbol,
      'for currency:',
      priceData.currency,
    );

    // Consumer plans: admin payment link (decode-price backfills the server defaults). No
    // client-side price fallbacks: a missing price renders empty, never an invented amount.
    this.oneTimePrice = priceData.oneTimePrice || priceData.price;
    this.annualPrice = priceData.annualPrice || '';
    this.monthlyPrice = priceData.monthlyPrice || '';
    this.annualSavingLabel = (priceData.annualSavingLabel || '').trim();

    console.log('PaymentComponent: consumer plans configured', {
      oneTimePrice: this.oneTimePrice,
      annualPrice: this.annualPrice,
      monthlyPrice: this.monthlyPrice,
      annualSavingLabel: this.annualSavingLabel,
    });

    this.gtmAnalytics.trackPricingViewed();
    this.pricesLoaded = true; // prices are in → reveal the plan cards
  }

  get hasStoreProducts(): boolean {
    return !!(this.storeProducts.onetime || this.storeProducts.monthly || this.storeProducts.annual);
  }

  /** "₪14.90" in LTR, "14.90 ₪" in Hebrew; empty when there is no real price. */
  money(v: unknown): string {
    const n = priceNum(v);
    if (n === null) return '';
    const amount = n.toFixed(2);
    return this.translationService.isRTL()
      ? `${amount} ${this.currencySymbol}`
      : `${this.currencySymbol}${amount}`;
  }

  /** Amount actually charged per cycle for the selected subscription. */
  get subPrimary(): string {
    if (this.storeMode) return this.storeProducts[this.selectedBilling]?.price || '';
    return this.selectedBilling === 'annual'
      ? this.money(webAnnualTotal(priceNum(this.annualPrice)))
      : this.money(this.monthlyPrice);
  }
  get subPerKey(): string {
    return this.selectedBilling === 'annual'
      ? 'payment.subscription.perYear'
      : 'payment.subscription.perMonth';
  }

  /** Annual plan's monthly equivalent, from the same platform and currency. */
  get annualEquivalent(): string {
    let price = '';
    if (this.storeMode) {
      const a = this.storeProducts.annual;
      const y = priceNum(a?.priceAmount);
      if (a && y) price = fmtStoreAmount(round2(y / 12), a.currency, this.translationService.isRTL());
    } else {
      price = this.money(this.annualPrice);
    }
    return price
      ? this.translationService.translate('payment.subscription.annualEquivalent', { price })
      : '';
  }

  /** Whole-percent annual saving; null (badge hidden) when not reliably known. */
  get annualSavePct(): number | null {
    if (this.storeMode) {
      const pair = storePair(this.storeProducts.annual, this.storeProducts.monthly);
      return pair ? savePct(pair.yearly, pair.monthly) : null;
    }
    return savePct(webAnnualTotal(priceNum(this.annualPrice)), priceNum(this.monthlyPrice));
  }

  get oneTimeDisplay(): string {
    return this.storeMode ? this.storeProducts.onetime?.price || '' : this.money(this.oneTimePrice);
  }

  get storeName(): string {
    return this.translationService.translate(
      this.store.isIOS ? 'payment.store.appStore' : 'payment.store.googlePlay',
    );
  }

  private afterStorePurchase(): void {
    if (this.scanId) this.router.navigate(['/app/results', this.scanId]);
    else this.router.navigate(['/app/home']);
  }

  async storeBuy(plan: StorePlan) {
    if (this.isProcessing) return;
    this.isProcessing = true;
    this.processingPlanId = plan;
    this.storeMessage = '';
    try {
      const result = await this.store.buy(plan);
      if (result === 'paid') {
        this.afterStorePurchase();
        return;
      }
      if (result !== 'cancelled') {
        const key =
          result === 'pending'
            ? 'payment.store.pending'
            : result === 'other_account'
              ? 'payment.store.otherAccount'
              : 'payment.store.failed';
        this.storeMessage = this.translationService.translate(key);
      }
    } finally {
      this.isProcessing = false;
      this.processingPlanId = null;
    }
  }

  async storeRestore() {
    if (this.isProcessing || this.restoring) return;
    this.restoring = true;
    this.isProcessing = true;
    this.storeMessage = '';
    try {
      if (await this.store.restore(true)) {
        this.afterStorePurchase();
        return;
      }
      this.storeMessage = this.translationService.translate('payment.store.restoreNone');
    } finally {
      this.restoring = false;
      this.isProcessing = false;
    }
  }

  setBilling(billing: 'annual' | 'monthly') {
    this.selectedBilling = billing;
  }

  // One-time 30-day pass: a Checkout Order (planType 'onetime') that grants the
  // full-access window (no auto-renew). Reuses the existing order flow.
  async selectOneTime() {
    return this.selectPlan('onetime');
  }

  // Recurring subscription: create a PayPal subscription for the selected
  // billing cadence and redirect to its approval URL.
  async selectSubscription() {
    if (this.isProcessing) return;
    const planType = this.selectedBilling;
    this.isProcessing = true;
    this.processingPlanId = planType;
    this.reporter.logEvent('payment.subscription.start', { planType });
    try {
      const origin = window.location.origin;
      const response: any = await this.restService.request({
        path: 'payment/create-subscription',
        method: 'POST',
        body: {
          planType,
          origin,
          scanId: this.scanId || undefined,
          lang: this.translationService.getCurrentLanguage(),
        },
      });
      if (response?.approvalUrl) {
        this.reporter.logEvent('payment.subscription.ok', { planType });
        window.location.href = response.approvalUrl;
      } else {
        console.error('PaymentComponent: no subscription approval URL received');
        this.reporter.report(
          'payment subscription: no approval URL received',
          'payment',
        );
        alert(this.translationService.translate('priceUi.payPalOrderFailed'));
      }
    } catch (error) {
      console.error('PaymentComponent: error creating subscription:', error);
      this.reporter.report(error, 'payment');
      alert(this.translationService.translate('priceUi.genericError'));
    } finally {
      this.isProcessing = false;
      this.processingPlanId = null;
    }
  }

  async selectPlan(planId: string) {
    if (this.isProcessing) {
      console.log('PaymentComponent: already processing, ignoring click');
      return;
    }

    console.log('PaymentComponent: plan selected', {
      planId,
      scanId: this.scanId,
    });

    this.isProcessing = true;
    this.processingPlanId = planId;

    this.reporter.logEvent('payment.order.start', { planId });

    try {
      console.log(
        'PaymentComponent: creating PayPal order for scanId:',
        this.scanId,
        'planType:',
        planId,
      );

      const origin = window.location.origin;

      const response: any = await this.restService.request({
        path: 'payment/create-paypal-order',
        method: 'POST',
        body: {
          scanId: this.scanId,
          planType: planId,
          origin: origin,
          lang: this.translationService.getCurrentLanguage(),
        },
      });

      console.log('PaymentComponent: PayPal order created', response);

      if (response?.error?.message === 'NO_SCAN') {
        alert(this.translationService.translate('priceUi.passNeedsScan'));
        return;
      }

      if (response.error?.message === 'Order already paid') {
        console.log(
          'PaymentComponent: Order already paid, redirecting to results',
        );
        this.router.navigate(['/app/results', this.scanId]);
        return;
      }

      if (response?.approvalUrl) {
        console.log('PaymentComponent: redirecting to PayPal approval URL');
        this.reporter.logEvent('payment.order.ok', { planId });
        window.location.href = response.approvalUrl;
      } else {
        console.error('PaymentComponent: no approval URL received');
        this.reporter.report('payment order: no approval URL received', 'payment');
        alert(this.translationService.translate('priceUi.payPalOrderFailed'));
      }
    } catch (error) {
      console.error('PaymentComponent: error creating PayPal order:', error);
      this.reporter.report(error, 'payment');
      alert(this.translationService.translate('priceUi.genericError'));
    } finally {
      this.isProcessing = false;
      this.processingPlanId = null;
    }
  }

  isBundlePlan(plan: any): plan is BundleConfig {
    return 'numberOfTests' in plan && 'numberOfMonths' in plan;
  }
}
